import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { User, Lock, Save } from "lucide-react";
import { handleGet, handleUpdate } from "../../controllers/controller.all";
import SubmitButton from "../../components/ui/SubmitButton";


export default function Profile() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const dispatch = useDispatch();

    const [loadingInfo, setLoadingInfo] = useState(false);
    const [loadingPwd, setLoadingPwd] = useState(false);

    const [infoData, setInfoData] = useState({
        nom: "",
        prenom: "",
        email: "",
        tel: "",
        adresse: ""
    });

    const [pwdData, setPwdData] = useState({
        oldPassword: "",
        password: "",
        confirmPassword: ""
    });

    useEffect(() => {
        const fetchUser = async () => {
            if (!user?._id) return;
            await handleGet(`/user/${user._id}`, (res) => {
                if (res?.user) {
                    setInfoData({
                        nom: res.user.nom || "",
                        prenom: res.user.prenom || "",
                        email: res.user.email || "",
                        tel: res.user.tel || "",
                        adresse: res.user.adresse || ""
                    });
                }
            }, token);
        };
        fetchUser();
    }, [user?._id, token]);

    const handleInfoChange = (e) => {
        setInfoData({ ...infoData, [e.target.name]: e.target.value });
    };

    const handlePwdChange = (e) => {
        setPwdData({ ...pwdData, [e.target.name]: e.target.value });
    };

    const handleInfoSubmit = async (e) => {
        e.preventDefault();
        const res = await handleUpdate(e, `/user/${user._id}`, infoData, setLoadingInfo, token);
        // Mettre à jour le store Redux si la modification a réussi
        if (res && res.state === "success") {
            dispatch({ type: "user-update", value: { ...user, ...infoData } });
        }
    };

    const handlePwdSubmit = async (e) => {
        e.preventDefault();
        if (pwdData.password !== pwdData.confirmPassword) {
            alert("Les mots de passe ne correspondent pas !");
            return;
        }
        const payload = {
            oldPassword: pwdData.oldPassword,
            password: pwdData.password
        };
        const res = await handleUpdate(e, `/user/password/${user._id}`, payload, setLoadingPwd, token);
        if (res && res.state === "success") {
            setPwdData({ oldPassword: "", password: "", confirmPassword: "" });
        }
    };

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Mon Profil</h1>
                <p className="text-sm text-gray-500 mt-1">Gérez vos informations personnelles et paramètres de sécurité.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Informations Personnelles */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 flex items-center gap-3 bg-gray-50">
                        <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                            <User className="h-5 w-5" />
                        </div>
                        <h2 className="font-semibold text-gray-900">Informations Personnelles</h2>
                    </div>
                    <form onSubmit={handleInfoSubmit} className="p-6 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Nom *</label>
                                <input required type="text" name="nom" value={infoData.nom} onChange={handleInfoChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Prénom *</label>
                                <input required type="text" name="prenom" value={infoData.prenom} onChange={handleInfoChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Email</label>
                            <input type="email" name="email" value={infoData.email} onChange={handleInfoChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Téléphone</label>
                            <input type="text" name="tel" value={infoData.tel} onChange={handleInfoChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Adresse</label>
                            <input type="text" name="adresse" value={infoData.adresse} onChange={handleInfoChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        <div className="pt-2 flex justify-end">
                            <SubmitButton type="submit" isSubmitting={loadingInfo} disabled={loadingInfo} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition">
                                <Save className="h-4 w-4" />
                                Enregistrer
                            </SubmitButton>
                        </div>
                    </form>
                </div>

                {/* Mot de passe */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-max">
                    <div className="p-4 border-b border-gray-200 flex items-center gap-3 bg-gray-50">
                        <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                            <Lock className="h-5 w-5" />
                        </div>
                        <h2 className="font-semibold text-gray-900">Sécurité</h2>
                    </div>
                    <form onSubmit={handlePwdSubmit} className="p-6 space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Ancien Mot de Passe *</label>
                            <input required type="password" name="oldPassword" value={pwdData.oldPassword} onChange={handlePwdChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Nouveau Mot de Passe *</label>
                            <input required type="password" name="password" value={pwdData.password} onChange={handlePwdChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500" />
                            <p className="text-xs text-gray-500">Doit contenir au moins 8 caractères, une majuscule, un chiffre et un caractère spécial.</p>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Confirmer le Mot de Passe *</label>
                            <input required type="password" name="confirmPassword" value={pwdData.confirmPassword} onChange={handlePwdChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        <div className="pt-2 flex justify-end">
                            <SubmitButton type="submit" isSubmitting={loadingPwd} disabled={loadingPwd} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition">
                                <Save className="h-4 w-4" />
                                Mettre à jour
                            </SubmitButton>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
